import { Injectable, Inject, Optional } from "@angular/core";
import { API_BASE_URL } from "./service-proxies";
import {
  mergeMap as _observableMergeMap,
  catchError as _observableCatch,
} from "rxjs/operators";
import { Observable,
  throwError as _observableThrow,
  of as _observableOf, } from "rxjs";
import { HttpClient,
  HttpHeaders,
  HttpResponse,
  HttpResponseBase, } from "@angular/common/http";


@Injectable()
export class ReminderServiceProxy {
  private http: HttpClient;
  private baseUrl: string;
  protected jsonParseReviver: ((key: string, value: any) => any) | undefined =
    undefined;

  constructor(
    @Inject(HttpClient) http: HttpClient,
    @Optional() @Inject(API_BASE_URL) baseUrl?: string
  ) {
    this.http = http;
    this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : "";
  }

  /**
   * @param body (optional)
   * @return Success
   */
  create(body: CreateReminderDto | undefined): Observable<ReminderDto> {
    let url_ = this.baseUrl + "/api/services/app/Reminder/Create";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processCreate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processCreate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ReminderDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<ReminderDto>;
        })
      );
  }

  protected processCreate(
    response: HttpResponseBase
  ): Observable<ReminderDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ReminderDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  delete(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/Reminder/Delete?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("delete", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processDelete(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processDelete(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processDelete(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  viewReminder(id: number | undefined): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/Reminder/ViewReminder?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "ReminderId=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processViewReminder(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processViewReminder(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processViewReminder(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  snoozeReminder(id: number | undefined, SnoozeMinutes: number): Observable<void> {
    let url_ = this.baseUrl + "/api/services/app/Reminder/SnoozeReminder?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "ReminderId=" + encodeURIComponent("" + id) + "&";
    if (SnoozeMinutes === null) throw new Error("The parameter 'SnoozeMinutes' cannot be null.");
    else if (SnoozeMinutes !== undefined)
      url_ += "SnoozeMinutes=" + encodeURIComponent("" + SnoozeMinutes) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({}),
    };

    return this.http
      .request("post", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processSnoozeReminder(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processSnoozeReminder(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<void>;
            }
          } else return _observableThrow(response_) as any as Observable<void>;
        })
      );
  }

  protected processSnoozeReminder(response: HttpResponseBase): Observable<void> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return _observableOf(null as any);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param id (optional)
   * @return Success
   */
  get(id: number | undefined): Observable<ReminderDto> {
    let url_ = this.baseUrl + "/api/services/app/Reminder/Get?";
    if (id === null) throw new Error("The parameter 'id' cannot be null.");
    else if (id !== undefined)
      url_ += "Id=" + encodeURIComponent("" + id) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGet(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGet(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ReminderDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<ReminderDto>;
        })
      );
  }

  protected processGet(
    response: HttpResponseBase
  ): Observable<ReminderDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ReminderDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param keyword (optional)
   * @param isActive (optional)
   * @param skipCount (optional)
   * @param maxResultCount (optional)
   * @return Success
   */
  getAll(
    keyword: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined
  ): Observable<ReminderDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/Reminder/GetAll?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<ReminderDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<ReminderDtoPagedResultDto>;
        })
      );
  }

  getAllIncoming(
    keyword: string | undefined,
    skipCount: number | undefined,
    maxResultCount: number | undefined
  ): Observable<ReminderDtoPagedResultDto> {
    let url_ = this.baseUrl + "/api/services/app/Reminder/GetAllIncoming?";
    if (keyword === null)
      throw new Error("The parameter 'keyword' cannot be null.");
    else if (keyword !== undefined)
      url_ += "Keyword=" + encodeURIComponent("" + keyword) + "&";
    if (skipCount === null)
      throw new Error("The parameter 'skipCount' cannot be null.");
    else if (skipCount !== undefined)
      url_ += "SkipCount=" + encodeURIComponent("" + skipCount) + "&";
    if (maxResultCount === null)
      throw new Error("The parameter 'maxResultCount' cannot be null.");
    else if (maxResultCount !== undefined)
      url_ += "MaxResultCount=" + encodeURIComponent("" + maxResultCount) + "&";
    url_ = url_.replace(/[?&]$/, "");

    let options_: any = {
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("get", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processGetAll(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processGetAll(response_ as any);
            } catch (e) {
              return _observableThrow(
                e
              ) as any as Observable<ReminderDtoPagedResultDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<ReminderDtoPagedResultDto>;
        })
      );
  }

  protected processGetAll(
    response: HttpResponseBase
  ): Observable<ReminderDtoPagedResultDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ReminderDtoPagedResultDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }

  /**
   * @param body (optional)
   * @return Success
   */
  update(body: CreateReminderDto | undefined): Observable<ReminderDto> {
    let url_ = this.baseUrl + "/api/services/app/Reminder/Update";
    url_ = url_.replace(/[?&]$/, "");

    const content_ = JSON.stringify(body);

    let options_: any = {
      body: content_,
      observe: "response",
      responseType: "blob",
      headers: new HttpHeaders({
        "Content-Type": "application/json-patch+json",
        Accept: "text/plain",
      }),
    };

    return this.http
      .request("put", url_, options_)
      .pipe(
        _observableMergeMap((response_: any) => {
          return this.processUpdate(response_);
        })
      )
      .pipe(
        _observableCatch((response_: any) => {
          if (response_ instanceof HttpResponseBase) {
            try {
              return this.processUpdate(response_ as any);
            } catch (e) {
              return _observableThrow(e) as any as Observable<ReminderDto>;
            }
          } else
            return _observableThrow(
              response_
            ) as any as Observable<ReminderDto>;
        })
      );
  }

  protected processUpdate(
    response: HttpResponseBase
  ): Observable<ReminderDto> {
    const status = response.status;
    const responseBlob =
      response instanceof HttpResponse
        ? response.body
        : (response as any).error instanceof Blob
        ? (response as any).error
        : undefined;

    let _headers: any = {};
    if (response.headers) {
      for (let key of response.headers.keys()) {
        _headers[key] = response.headers.get(key);
      }
    }
    if (status === 200) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          let result200: any = null;
          let resultData200 =
            _responseText === ""
              ? null
              : JSON.parse(_responseText, this.jsonParseReviver);
          result200 = ReminderDto.fromJS(resultData200);
          return _observableOf(result200);
        })
      );
    } else if (status !== 200 && status !== 204) {
      return blobToText(responseBlob).pipe(
        _observableMergeMap((_responseText: string) => {
          return throwException(
            "An unexpected server error occurred.",
            status,
            _responseText,
            _headers
          );
        })
      );
    }
    return _observableOf(null as any);
  }
}

export class ReminderDto implements IReminderDto {
  id: number;
  title: string;
  executionTime: string;
  remindTime: string;
  snoozed: boolean;
  snoozTime: string;
  ownerUserId: number;
  ownerUserFullName: string;
  relatedUserId: number;
  relatedUserFullName: string;
  notifyUser: boolean;
  sendEmail: boolean;
  sendSms: boolean;
  description: string;
  viewed: boolean;
  relatedUserViewed: boolean;
  isOwner: boolean;
  isExpired: boolean;
  isInNotifyTime: boolean;

  constructor(data?: IReminderDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.title = _data["title"];
      this.executionTime = _data["executionTime"];
      this.remindTime = _data["remindTime"];
      this.snoozed = _data["snoozed"];
      this.snoozTime = _data["snoozTime"];
      this.ownerUserId = _data["ownerUserId"];
      this.ownerUserFullName = _data["ownerUserFullName"];
      this.relatedUserId = _data["relatedUserId"];
      this.relatedUserFullName = _data["relatedUserFullName"];
      this.notifyUser = _data["notifyUser"];
      this.sendEmail = _data["sendEmail"];
      this.sendSms = _data["sendSms"];
      this.description = _data["description"];
      this.viewed = _data["viewed"];
      this.relatedUserViewed = _data["relatedUserViewed"];
      this.isOwner = _data["isOwner"];
      this.isExpired = _data["isExpired"];
      this.isInNotifyTime = _data["isInNotifyTime"];
    }
  }

  static fromJS(data: any): ReminderDto {
    data = typeof data === "object" ? data : {};
    let result = new ReminderDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["title"] = this.title;
    data["executionTime"] = this.executionTime;
    data["remindTime"] = this.remindTime;
    data["snoozed"] = this.snoozed;
    data["snoozTime"] = this.snoozTime;
    data["ownerUserId"] = this.ownerUserId;
    data["ownerUserFullName"] = this.ownerUserFullName;
    data["relatedUserId"] = this.relatedUserId;
    data["relatedUserFullName"] = this.relatedUserFullName;
    data["notifyUser"] = this.notifyUser;
    data["sendEmail"] = this.sendEmail;
    data["sendSms"] = this.sendSms;
    data["description"] = this.description;
    data["viewed"] = this.viewed;
    data["relatedUserViewed"] = this.relatedUserViewed;
    data["isOwner"] = this.isOwner;
    data["isExpired"] = this.isExpired;
    data["isInNotifyTime"] = this.isInNotifyTime;
    return data;
  }

  clone(): ReminderDto {
    const json = this.toJSON();
    let result = new ReminderDto();
    result.init(json);
    return result;
  }
}

export interface IReminderDto {
  id: number;
  title: string;
  executionTime: string;
  remindTime: string;
  snoozed: boolean;
  snoozTime: string;
  ownerUserId: number;
  ownerUserFullName: string;
  relatedUserId: number;
  relatedUserFullName: string;
  notifyUser: boolean;
  sendEmail: boolean;
  sendSms: boolean;
  description: string;
  viewed: boolean;
  relatedUserViewed: boolean;
  isOwner: boolean;
  isExpired: boolean;
  isInNotifyTime: boolean;
}

export class CreateReminderDto implements ICreateReminderDto {
  id: number;
  title: string;
  executionTime: string;
  remindBeforeHours: number;
  relatedUserId: number;
  notifyUser: boolean;
  sendEmail: boolean;
  sendSms: boolean;
  description: string;

  constructor(data?: ICreateReminderDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      this.id = _data["id"];
      this.title = _data["title"];
      this.executionTime = _data["executionTime"];
      this.remindBeforeHours = _data["remindBeforeHours"];
      this.relatedUserId = _data["relatedUserId"];
      this.notifyUser = _data["notifyUser"];
      this.sendEmail = _data["sendEmail"];
      this.sendSms = _data["sendSms"];
      this.description = _data["description"];
    }
  }

  static fromJS(data: any): CreateReminderDto {
    data = typeof data === "object" ? data : {};
    let result = new CreateReminderDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    data["id"] = this.id;
    data["title"] = this.title;
    data["executionTime"] = this.executionTime;
    data["remindBeforeHours"] = this.remindBeforeHours;
    data["relatedUserId"] = this.relatedUserId;
    data["notifyUser"] = this.notifyUser;
    data["sendEmail"] = this.sendEmail;
    data["sendSms"] = this.sendSms;
    data["description"] = this.description;
    return data;
  }

  clone(): CreateReminderDto {
    const json = this.toJSON();
    let result = new CreateReminderDto();
    result.init(json);
    return result;
  }
}

export interface ICreateReminderDto {
  id: number;
  title: string;
  executionTime: string;
  remindBeforeHours: number;
  relatedUserId: number;
  notifyUser: boolean;
  sendEmail: boolean;
  sendSms: boolean;
  description: string;
}

export class ReminderDtoPagedResultDto
  implements IReminderDtoPagedResultDto
{
  items: ReminderDto[] | undefined;
  totalCount: number;

  constructor(data?: IReminderDtoPagedResultDto) {
    if (data) {
      for (var property in data) {
        if (data.hasOwnProperty(property))
          (<any>this)[property] = (<any>data)[property];
      }
    }
  }

  init(_data?: any) {
    if (_data) {
      if (Array.isArray(_data["items"])) {
        this.items = [] as any;
        for (let item of _data["items"])
          this.items.push(ReminderDto.fromJS(item));
      }
      this.totalCount = _data["totalCount"];
    }
  }

  static fromJS(data: any): ReminderDtoPagedResultDto {
    data = typeof data === "object" ? data : {};
    let result = new ReminderDtoPagedResultDto();
    result.init(data);
    return result;
  }

  toJSON(data?: any) {
    data = typeof data === "object" ? data : {};
    if (Array.isArray(this.items)) {
      data["items"] = [];
      for (let item of this.items) data["items"].push(item.toJSON());
    }
    data["totalCount"] = this.totalCount;
    return data;
  }

  clone(): ReminderDtoPagedResultDto {
    const json = this.toJSON();
    let result = new ReminderDtoPagedResultDto();
    result.init(json);
    return result;
  }
}

export interface IReminderDtoPagedResultDto {
  items: ReminderDto[] | undefined;
  totalCount: number;
}

function blobToText(blob: any): Observable<string> {
  return new Observable<string>((observer: any) => {
    if (!blob) {
      observer.next("");
      observer.complete();
    } else {
      let reader = new FileReader();
      reader.onload = (event) => {
        observer.next((event.target as any).result);
        observer.complete();
      };
      reader.readAsText(blob);
    }
  });
}

export class ApiException extends Error {
  message: string;
  status: number;
  response: string;
  headers: { [key: string]: any };
  result: any;

  constructor(
    message: string,
    status: number,
    response: string,
    headers: { [key: string]: any },
    result: any
  ) {
    super();

    this.message = message;
    this.status = status;
    this.response = response;
    this.headers = headers;
    this.result = result;
  }

  protected isApiException = true;

  static isApiException(obj: any): obj is ApiException {
    return obj.isApiException === true;
  }
}

function throwException(
  message: string,
  status: number,
  response: string,
  headers: { [key: string]: any },
  result?: any
): Observable<any> {
  if (result !== null && result !== undefined) return _observableThrow(result);
  else
    return _observableThrow(
      new ApiException(message, status, response, headers, null)
    );
}
